import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import IconButton from 'components/@extended/IconButton';
import { metallicPalette } from 'themes/colors';

export const DRAWER_HEADER_HEIGHT = 42;

/**
 * Reusable Global Slim Drawer Header Component
 * Provides a fixed 42px slim header height, 1-line alignment, title, badge, right actions, and animated close button.
 */
export default function DrawerHeader({ title, count, countLabel = 'New', actions, onClose, sx, ...props }) {
  const [dark, setDark] = useState(() => localStorage.getItem('hrmsThemeMode') === 'dark');

  useEffect(() => {
    const handleThemeChange = () => setDark(localStorage.getItem('hrmsThemeMode') === 'dark');
    window.addEventListener('hrmsThemeChanged', handleThemeChange);
    return () => window.removeEventListener('hrmsThemeChanged', handleThemeChange);
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        height: DRAWER_HEADER_HEIGHT,
        minHeight: DRAWER_HEADER_HEIGHT,
        maxHeight: DRAWER_HEADER_HEIGHT,
        px: 2,
        bgcolor: dark ? '#111827' : metallicPalette.grey[1],
        borderBottom: `1px solid ${dark ? 'rgba(148, 163, 184, 0.18)' : metallicPalette.grey[3]}`,
        display: 'flex',
        alignItems: 'center',
        justify: 'space-between',
        flexShrink: 0,
        boxSizing: 'border-box',
        ...sx
      }}
      {...props}
    >
      {/* ── Left Side: Title & Badge ── */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 800,
            color: dark ? '#F8FAFC' : metallicPalette.grey[9],
            letterSpacing: -0.2,
            lineHeight: 1
          }}
        >
          {title}
        </Typography>

        {count !== undefined && count > 0 && (
          <Box
            sx={{
              px: 1,
              py: 0.2,
              bgcolor: dark ? 'rgba(255,255,255,0.08)' : alpha(metallicPalette.primary.main, 0.12),
              color: dark ? '#F8FAFC' : metallicPalette.primary.main,
              borderRadius: '10px',
              fontSize: 10.5,
              fontWeight: 700,
              lineHeight: 1
            }}
          >
            {count} {countLabel}
          </Box>
        )}
      </Stack>

      {/* ── Expanding Spacer to Push Right Actions to Far Right Edge ── */}
      <Box sx={{ flexGrow: 1 }} />

      {/* ── Right Side: Action Buttons & Far Right-Most Animated Close Icon ── */}
      <Stack direction="row" spacing={1} alignItems="center">
        {actions}

        {onClose && (
          <Tooltip title="Close">
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                color: dark ? '#CBD5E1' : metallicPalette.grey[7],
                bgcolor: dark ? '#1E293B' : alpha(metallicPalette.grey[4], 0.18),
                border: `1px solid ${dark ? 'rgba(148, 163, 184, 0.18)' : alpha(metallicPalette.grey[4], 0.3)}`,
                transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                '&:hover': {
                  bgcolor: dark ? 'rgba(239, 68, 68, 0.12)' : alpha('#EF4444', 0.15),
                  color: '#EF4444',
                  borderColor: alpha('#EF4444', 0.35),
                  transform: 'rotate(90deg) scale(1.12)'
                },
                '&:active': {
                  transform: 'rotate(180deg) scale(0.9)'
                }
              }}
            >
              <CloseOutlined style={{ fontSize: 12, fontWeight: 'bold' }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}
